import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import {
  Upload, FileText, Users, CheckCircle, Clock, Link2, Copy, Eye,
  Loader2, Trash2, UserPlus, FileSpreadsheet, Search, AlertTriangle,
  BarChart3, Download, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const PIE_COLORS = ['#10b981', '#f59e0b', '#ef4444'];

export default function PolicyAckDashboard() {
  const { user, viewingOrg, token: authToken } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canManageExtUsers = isAdmin || user?.role === 'client' || user?.role === 'auditor';
  const vtq = () => (viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '');

  const [policies, setPolicies] = useState([]);
  const [extUsers, setExtUsers] = useState([]);
  const [trackingSummary, setTrackingSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [uploadPdfOpen, setUploadPdfOpen] = useState(null); // policy object
  const [importUsersOpen, setImportUsersOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null); // policy object
  const [trackingOpen, setTrackingOpen] = useState(null); // policy_id
  const [trackingData, setTrackingData] = useState(null);

  // PDF preview
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(null); // policy object
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [userFile, setUserFile] = useState(null);
  const [importingUsers, setImportingUsers] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Assign state
  const [assignAll, setAssignAll] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [assigning, setAssigning] = useState(false);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // ---- Bundle (Assign All Policies) ----
  const [bundles, setBundles] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAssignAll, setBulkAssignAll] = useState(true);
  const [bulkSelectedUserIds, setBulkSelectedUserIds] = useState([]);
  const [bulkName, setBulkName] = useState('');
  const [bulkIncludeAllPolicies, setBulkIncludeAllPolicies] = useState(true);
  const [bulkSelectedPolicyIds, setBulkSelectedPolicyIds] = useState([]);
  const [bulkPolicySearch, setBulkPolicySearch] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // {bundle_token, ...}
  const [bundleTrackingOpen, setBundleTrackingOpen] = useState(null); // bundle id
  const [bundleTrackingData, setBundleTrackingData] = useState(null);

  const fetchBundles = async () => {
    try {
      const r = await axios.get(`${API}/policy-ack/bundles${vtq()}`);
      setBundles(r.data);
    } catch { /* silent */ }
  };

  useEffect(() => { if (!loading) fetchBundles(); }, [viewingOrg, loading]);  // eslint-disable-line

  // ---- Manage Users ----
  const [manageUsersOpen, setManageUsersOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [deletingUserId, setDeletingUserId] = useState(null);

  // ---- Export Report ----
  const [exporting, setExporting] = useState(false);

  const fmtDateTime = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const fetchReport = async () => {
    const r = await axios.get(`${API}/policy-ack/tracking/report${vtq()}`);
    return r.data;
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const data = await fetchReport();
      if (!data.rows || data.rows.length === 0) {
        toast.error('No acknowledgement records to export');
        return;
      }
      const sheetRows = data.rows.map(r => ({
        'Policy ID': r.policy_code,
        'Policy Title': r.policy_title,
        'Category': r.category,
        'Framework': r.framework,
        'Assignment': r.assignment_type,
        'Bundle': r.bundle_name,
        'User Name': r.user_name,
        'User Email': r.user_email,
        'Status': r.status === 'acknowledged' ? 'Acknowledged' : 'Pending',
        'Assigned At': fmtDateTime(r.assigned_at),
        'Acknowledged At': fmtDateTime(r.acknowledged_at),
      }));
      const summaryRows = [
        { Metric: 'Organization', Value: data.organization },
        { Metric: 'Generated At', Value: fmtDateTime(data.generated_at) },
        { Metric: 'Total Records', Value: data.total_records },
        { Metric: 'Acknowledged', Value: data.acknowledged_records },
        { Metric: 'Pending', Value: data.pending_records },
        { Metric: 'Completion %', Value: `${data.completion_pct}%` },
      ];
      const wb = XLSX.utils.book_new();
      const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
      const wsData = XLSX.utils.json_to_sheet(sheetRows);
      // Set column widths for better readability
      wsData['!cols'] = [
        { wch: 12 }, { wch: 36 }, { wch: 18 }, { wch: 12 },
        { wch: 12 }, { wch: 24 }, { wch: 22 }, { wch: 28 },
        { wch: 14 }, { wch: 22 }, { wch: 22 },
      ];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
      XLSX.utils.book_append_sheet(wb, wsData, 'Acknowledgements');
      const fname = `Policy_Acknowledgement_Report_${(data.organization || 'Org').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success('Excel report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const data = await fetchReport();
      if (!data.rows || data.rows.length === 0) {
        toast.error('No acknowledgement records to export');
        return;
      }
      const orgName = data.organization || 'Organization';
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Header band
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageWidth, 48, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(orgName, 40, 30);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('Policy Acknowledgement Report', pageWidth - 40, 30, { align: 'right' });

      // Summary
      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const generatedTxt = `Generated: ${fmtDateTime(data.generated_at)}`;
      const summaryTxt = `Total: ${data.total_records}   |   Acknowledged: ${data.acknowledged_records}   |   Pending: ${data.pending_records}   |   Completion: ${data.completion_pct}%`;
      doc.text(generatedTxt, 40, 70);
      doc.setFont('helvetica', 'bold');
      doc.text(summaryTxt, 40, 86);
      doc.setFont('helvetica', 'normal');

      // Table
      autoTable(doc, {
        startY: 100,
        head: [['Policy ID', 'Policy Title', 'Type', 'User Name', 'Email', 'Status', 'Assigned', 'Acknowledged']],
        body: data.rows.map(r => [
          r.policy_code || '-',
          r.policy_title || '-',
          r.assignment_type,
          r.user_name || '-',
          r.user_email,
          r.status === 'acknowledged' ? 'Acknowledged' : 'Pending',
          fmtDateTime(r.assigned_at),
          fmtDateTime(r.acknowledged_at),
        ]),
        styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
        headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 160 },
          2: { cellWidth: 55 },
          3: { cellWidth: 100 },
          4: { cellWidth: 130 },
          5: { cellWidth: 70 },
          6: { cellWidth: 100 },
          7: { cellWidth: 100 },
        },
        didParseCell: (h) => {
          if (h.section === 'body' && h.column.index === 5) {
            const txt = (h.cell.raw || '').toString().toLowerCase();
            if (txt === 'acknowledged') {
              h.cell.styles.textColor = [5, 150, 105];
              h.cell.styles.fontStyle = 'bold';
            } else if (txt === 'pending') {
              h.cell.styles.textColor = [217, 119, 6];
            }
          }
        },
        didDrawPage: () => {
          // Footer on every page
          const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
          const totalPages = doc.internal.getNumberOfPages();
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(148, 163, 184);
          doc.text(`${orgName} · Policy Acknowledgement Report`, 40, pageHeight - 20);
          doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 40, pageHeight - 20, { align: 'right' });
          doc.setTextColor(15, 23, 42);
        },
      });

      const fname = `Policy_Acknowledgement_Report_${orgName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(fname);
      toast.success('PDF report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}? This will also remove their pending acknowledgements (completed acknowledgements remain for audit).`)) return;
    setDeletingUserId(u.id);
    try {
      await axios.delete(`${API}/policy-ack/users/${u.id}${vtq()}`);
      toast.success('User deleted');
      // Refresh users + tracking summary so counts stay correct
      const [usersRes, trackRes] = await Promise.all([
        axios.get(`${API}/policy-ack/users${vtq()}`),
        axios.get(`${API}/policy-ack/tracking/summary${vtq()}`),
      ]);
      setExtUsers(usersRes.data);
      setTrackingSummary(trackRes.data);
      fetchBundles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const fetchAll = async () => {
    if (user?.role === 'super_admin' && !viewingOrg?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [polRes, usersRes, trackRes] = await Promise.all([
        axios.get(`${API}/policies${vtq()}`),
        axios.get(`${API}/policy-ack/users${vtq()}`),
        axios.get(`${API}/policy-ack/tracking/summary${vtq()}`),
      ]);
      setPolicies(polRes.data);
      setExtUsers(usersRes.data);
      setTrackingSummary(trackRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [viewingOrg]);

  // ---- PDF Upload ----
  const handlePdfUpload = async () => {
    if (!pdfFile || !uploadPdfOpen) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pdfFile);
      await axios.post(`${API}/policy-ack/policies/${uploadPdfOpen.id}/upload-pdf${vtq()}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('PDF uploaded');
      setUploadPdfOpen(null);
      setPdfFile(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ---- Import Users ----
  const handleImportUsers = async () => {
    if (!userFile) return;
    setImportingUsers(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', userFile);
      const res = await axios.post(`${API}/policy-ack/users/import${vtq()}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.added} users`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImportingUsers(false);
    }
  };

  // ---- Assign ----
  const handleAssign = async () => {
    if (!assignOpen) return;
    setAssigning(true);
    try {
      const body = assignAll ? { assign_all: true } : { user_ids: selectedUserIds };
      const res = await axios.post(`${API}/policy-ack/policies/${assignOpen.id}/assign${vtq()}`, body);
      if (res.data.assigned > 0) {
        toast.success(`Assigned to ${res.data.assigned} new users`);
      } else if (res.data.already_assigned > 0) {
        toast.info(`All ${res.data.already_assigned} users were already assigned to this policy`);
      } else {
        toast.info('No users to assign');
      }
      if (res.data.access_code) {
        toast.success(`Access code: ${res.data.access_code}`, { duration: 10000 });
      }
      setAssignOpen(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  // ---- Tracking ----
  const openTracking = async (policyId) => {
    setTrackingOpen(policyId);
    setTrackingData(null);
    try {
      const res = await axios.get(`${API}/policy-ack/policies/${policyId}/tracking${vtq()}`);
      setTrackingData(res.data);
    } catch {
      toast.error('Failed to load tracking data');
    }
  };

  const copyLink = (ackToken, accessCode) => {
    const url = `${window.location.origin}/policy-acknowledge/${ackToken}`;
    const text = accessCode
      ? `${url}\nAccess code: ${accessCode}`
      : url;
    navigator.clipboard.writeText(text);
    toast.success(accessCode ? 'Link and access code copied' : 'Acknowledgement link copied');
  };

  const copyBundleLink = (bundleToken, accessCode) => {
    const url = `${window.location.origin}/policy-acknowledge/bundle/${bundleToken}`;
    const text = accessCode
      ? `${url}\nAccess code: ${accessCode}`
      : url;
    navigator.clipboard.writeText(text);
    toast.success(accessCode ? 'Bundle link and access code copied' : 'Bundle link copied');
  };

  const handleBulkAssign = async () => {
    if (policies.length === 0) {
      toast.error('No policies available. Please add policies first.');
      return;
    }
    if (!bulkIncludeAllPolicies && bulkSelectedPolicyIds.length === 0) {
      toast.error('Select at least one policy to include in the bundle');
      return;
    }
    if (!bulkAssignAll && bulkSelectedUserIds.length === 0) {
      toast.error('Select at least one user');
      return;
    }
    setBulkSubmitting(true);
    try {
      const body = {
        name: bulkName.trim(),
        assign_all_users: bulkAssignAll,
        user_ids: bulkAssignAll ? [] : bulkSelectedUserIds,
        policy_ids: bulkIncludeAllPolicies ? [] : bulkSelectedPolicyIds,
      };
      const res = await axios.post(`${API}/policy-ack/bundle/assign${vtq()}`, body);
      setBulkResult(res.data);
      toast.success(`Bundle created: ${res.data.total_policies} policies × ${res.data.assigned_users} users`);
      fetchBundles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create bundle');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const openBundleTracking = async (bundleId) => {
    setBundleTrackingOpen(bundleId);
    setBundleTrackingData(null);
    try {
      const r = await axios.get(`${API}/policy-ack/bundles/${bundleId}/tracking${vtq()}`);
      setBundleTrackingData(r.data);
    } catch {
      toast.error('Failed to load bundle tracking');
    }
  };

  const deleteBundle = async (bundleId) => {
    if (!window.confirm('Delete this bundle? Pending acknowledgements will be removed; completed ones will be preserved.')) return;
    try {
      await axios.delete(`${API}/policy-ack/bundles/${bundleId}${vtq()}`);
      toast.success('Bundle deleted');
      fetchBundles();
    } catch {
      toast.error('Failed to delete bundle');
    }
  };

  const getTrackingForPolicy = (policyId) => trackingSummary.find(t => t.policy_id === policyId);

  const filteredPolicies = policies.filter(p =>
    !searchTerm ||
    p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.policy_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (user?.role === 'super_admin' && !viewingOrg?.id) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center">
        <Users className="w-12 h-12 text-slate-300 mb-4" />
        <h2 className="text-lg font-semibold text-slate-700">Select an Organization</h2>
        <p className="text-slate-500 mt-1">Choose a tenant from the top bar to manage policy acknowledgements</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="policy-ack-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-slate-700" />
            Policy Acknowledgement
          </h1>
          <p className="text-slate-600 mt-1">Upload PDFs, assign policies, track acknowledgements</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManageExtUsers && (
            <Button variant="outline" onClick={() => { setImportUsersOpen(true); setUserFile(null); setImportResult(null); }} data-testid="import-users-btn">
              <UserPlus className="w-4 h-4 mr-2" />Import Users
            </Button>
          )}
          {isAdmin && (
            <>
              <Button
                onClick={() => {
                  setBulkOpen(true);
                  setBulkAssignAll(true);
                  setBulkSelectedUserIds([]);
                  setBulkName(`All Policies — ${new Date().toISOString().slice(0, 10)}`);
                  setBulkResult(null);
                }}
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="bulk-assign-all-btn"
              >
                <Link2 className="w-4 h-4 mr-2" />Assign All Policies
              </Button>
              <Button
                variant="outline"
                onClick={handleExportPdf}
                disabled={exporting}
                data-testid="export-report-pdf-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export PDF
              </Button>
              <Button
                variant="outline"
                onClick={handleExportExcel}
                disabled={exporting}
                data-testid="export-report-excel-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                Export Excel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* External Users Count */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-slate-800">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">External Users</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="ext-users-count">{extUsers.length}</p>
              </div>
              {canManageExtUsers && extUsers.length > 0 && (
                <button
                  onClick={() => { setManageUsersOpen(true); setUserSearch(''); }}
                  className="text-xs font-medium text-slate-600 hover:text-slate-900 underline"
                  data-testid="manage-users-btn"
                >
                  Manage
                </button>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Policies with Tracking</p>
            <p className="text-3xl font-bold text-blue-600 mt-1">{trackingSummary.length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Avg Completion</p>
            <p className="text-3xl font-bold text-emerald-600 mt-1">
              {trackingSummary.length ? Math.round(trackingSummary.reduce((s, t) => s + t.completion_pct, 0) / trackingSummary.length) : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Bundle Assignments */}
      {bundles.length > 0 && (
        <Card className="overflow-hidden" data-testid="bundles-card">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-slate-600" />
              <h3 className="text-sm font-semibold text-slate-800">Bundle Assignments</h3>
              <span className="text-xs text-slate-500">({bundles.length})</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white border-b border-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Bundle Name</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Policies</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Users</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Progress</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Created</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {bundles.map(b => (
                  <tr key={b.bundle_id} className="hover:bg-slate-50/60" data-testid={`bundle-row-${b.bundle_id}`}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-900">{b.name}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{b.total_policies}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      <span className="text-emerald-700 font-medium">{b.fully_acknowledged_users}</span>
                      <span className="text-slate-400"> / </span>
                      <span>{b.assigned_users}</span>
                    </td>
                    <td className="px-4 py-3 w-40">
                      <Progress value={b.completion_pct} className="h-2" />
                      <span className="text-xs text-slate-500 mt-0.5 block">{b.completion_pct}%</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {b.created_at ? new Date(b.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => copyBundleLink(b.bundle_token, b.access_code)}
                          className="text-slate-500 hover:text-slate-800"
                          title={b.access_code ? `Copy link + code (${b.access_code})` : 'Copy bundle link'}
                          data-testid={`copy-bundle-${b.bundle_id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openBundleTracking(b.bundle_id)}
                          className="text-slate-500 hover:text-slate-800"
                          title="View tracking"
                          data-testid={`track-bundle-${b.bundle_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => deleteBundle(b.bundle_id)}
                            className="text-slate-400 hover:text-red-600"
                            title="Delete bundle"
                            data-testid={`delete-bundle-${b.bundle_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search policies..." className="pl-10 bg-white border-slate-300" data-testid="policy-search" />
      </div>

      {/* Policies Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="policies-ack-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Policy</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">PDF</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Acknowledgement</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Progress</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPolicies.map((policy) => {
                const tracking = getTrackingForPolicy(policy.id);
                return (
                  <tr key={policy.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`policy-row-${policy.policy_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <div>
                          <span className="text-xs font-mono text-slate-500">{policy.policy_id}</span>
                          <p className="text-sm font-medium text-slate-900">{policy.title}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {policy.pdf_filename ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setPdfPreviewOpen(policy)}
                            className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                            title="View PDF"
                            data-testid={`view-pdf-${policy.policy_id}`}
                          >
                            <FileText className="w-4 h-4 text-emerald-600" />
                            <span className="text-xs">{policy.pdf_filename}</span>
                          </button>
                          {isAdmin && (
                            <button onClick={() => { setUploadPdfOpen(policy); setPdfFile(null); }} className="text-slate-400 hover:text-slate-600 text-xs underline">Replace</button>
                          )}
                        </div>
                      ) : (
                        isAdmin && (
                          <Button size="sm" variant="outline" onClick={() => { setUploadPdfOpen(policy); setPdfFile(null); }} className="text-xs" data-testid={`upload-pdf-${policy.policy_id}`}>
                            <Upload className="w-3 h-3 mr-1" />Upload PDF
                          </Button>
                        )
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {tracking ? (
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                            <span className="text-xs text-emerald-700">{tracking.acknowledged}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-amber-500" />
                            <span className="text-xs text-amber-600">{tracking.pending}</span>
                          </div>
                          <span className="text-xs text-slate-500">/ {tracking.total}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Not assigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 w-40">
                      {tracking && tracking.total > 0 ? (
                        <div>
                          <Progress value={tracking.completion_pct} className="h-2" />
                          <span className="text-xs text-slate-500 mt-0.5 block">{tracking.completion_pct}%</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1.5">
                        {isAdmin && extUsers.length > 0 && (
                          <Button size="sm" variant="outline" onClick={() => { setAssignOpen(policy); setAssignAll(true); setSelectedUserIds([]); }} className="text-xs" data-testid={`assign-${policy.policy_id}`}>
                            <Users className="w-3 h-3 mr-1" />Assign
                          </Button>
                        )}
                        {tracking?.ack_token && (
                          <button onClick={() => copyLink(tracking.ack_token, tracking.access_code)} className="text-slate-500 hover:text-slate-800" title={tracking.access_code ? `Copy link + code (${tracking.access_code})` : 'Copy acknowledgement link'}>
                            <Copy className="w-4 h-4" />
                          </button>
                        )}
                        {tracking && tracking.total > 0 && (
                          <button onClick={() => openTracking(policy.id)} className="text-slate-500 hover:text-slate-800" title="View tracking" data-testid={`track-${policy.policy_id}`}>
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Upload PDF Dialog */}
      <Dialog open={!!uploadPdfOpen} onOpenChange={() => setUploadPdfOpen(null)}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Upload Policy PDF</DialogTitle>
            <DialogDescription>{uploadPdfOpen?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center ${pdfFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300'}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.name.endsWith('.pdf')) setPdfFile(f); else toast.error('Only PDF files'); }}
            >
              {pdfFile ? (
                <div>
                  <FileText className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">{pdfFile.name}</p>
                  <button onClick={() => setPdfFile(null)} className="text-xs text-red-600 underline mt-1">Remove</button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Drop PDF here or <span className="text-blue-600 underline">browse</span></p>
                  <input type="file" accept=".pdf" onChange={(e) => setPdfFile(e.target.files[0])} className="hidden" />
                </label>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setUploadPdfOpen(null)}>Cancel</Button>
            <Button onClick={handlePdfUpload} disabled={!pdfFile || uploading} className="bg-slate-900 text-white hover:bg-slate-800">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Upload
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Users Dialog */}
      <Dialog open={importUsersOpen} onOpenChange={setImportUsersOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Import External Users</DialogTitle>
            <DialogDescription>Upload a CSV or XLSX file with Name and Email columns</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 border rounded-md p-3 text-xs text-slate-600">
              Required: <strong>Email</strong> column. Optional: <strong>Name</strong> column.
            </div>
            <div className={`border-2 border-dashed rounded-lg p-6 text-center ${userFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300'}`}>
              {userFile ? (
                <div>
                  <FileSpreadsheet className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">{userFile.name}</p>
                  <button onClick={() => setUserFile(null)} className="text-xs text-red-600 underline mt-1">Remove</button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <UserPlus className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Drop file or <span className="text-blue-600 underline">browse</span></p>
                  <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { setUserFile(e.target.files[0]); setImportResult(null); }} className="hidden" />
                </label>
              )}
            </div>
            {importResult && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
                <p className="font-medium text-emerald-800">Added: {importResult.added} | Skipped: {importResult.skipped_duplicates}</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setImportUsersOpen(false)}>Close</Button>
            {!importResult && (
              <Button onClick={handleImportUsers} disabled={!userFile || importingUsers} className="bg-slate-900 text-white hover:bg-slate-800">
                {importingUsers ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Import
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={!!assignOpen} onOpenChange={() => setAssignOpen(null)}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Policy</DialogTitle>
            <DialogDescription>{assignOpen?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={assignAll} onChange={() => setAssignAll(true)} className="h-4 w-4" />
              <span className="text-sm">Assign to all {extUsers.length} external users</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={!assignAll} onChange={() => setAssignAll(false)} className="h-4 w-4" />
              <span className="text-sm">Select specific users</span>
            </label>
            {!assignAll && (
              <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                {extUsers.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">
                    <input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={(e) => {
                      setSelectedUserIds(prev => e.target.checked ? [...prev, u.id] : prev.filter(id => id !== u.id));
                    }} className="h-3.5 w-3.5" />
                    <span className="font-medium">{u.name || '-'}</span>
                    <span className="text-slate-500">{u.email}</span>
                  </label>
                ))}
              </div>
            )}
            {!assignOpen?.pdf_filename && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <span>No PDF uploaded for this policy. Users will see the policy title and description only.</span>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setAssignOpen(null)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={assigning || (!assignAll && selectedUserIds.length === 0)} className="bg-slate-900 text-white hover:bg-slate-800">
              {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}Assign
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tracking Dialog */}
      <Dialog open={!!trackingOpen} onOpenChange={() => setTrackingOpen(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          {trackingData ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <BarChart3 className="w-5 h-5" />
                  {trackingData.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-2">
                {/* Summary row */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-900">{trackingData.total}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{trackingData.acknowledged}</p>
                    <p className="text-xs text-emerald-700">Acknowledged</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-600">{trackingData.pending}</p>
                    <p className="text-xs text-amber-700">Pending</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-900">{trackingData.completion_pct}%</p>
                    <p className="text-xs text-slate-500">Completion</p>
                  </div>
                </div>

                {/* Progress bar */}
                <div>
                  <Progress value={trackingData.completion_pct} className="h-3" />
                </div>

                {/* Chart */}
                {trackingData.total > 0 && (
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Acknowledged', value: trackingData.acknowledged },
                            { name: 'Pending', value: trackingData.pending },
                          ].filter(d => d.value > 0)}
                          cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {[0,1].map(i => <Cell key={i} fill={PIE_COLORS[i]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Copy link + Access code */}
                {trackingData.ack_token && (
                  <>
                    <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-3">
                      <Link2 className="w-4 h-4 text-slate-500" />
                      <code className="text-xs text-slate-700 flex-1 truncate">{window.location.origin}/policy-acknowledge/{trackingData.ack_token}</code>
                      <Button size="sm" variant="outline" onClick={() => copyLink(trackingData.ack_token, trackingData.access_code)} className="text-xs">
                        <Copy className="w-3 h-3 mr-1" />Copy
                      </Button>
                    </div>
                    {trackingData.access_code && (
                      <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <span className="text-xs font-medium text-slate-600 uppercase">Access code</span>
                        <code className="text-lg font-bold tracking-[0.35em] text-slate-900 flex-1 text-center font-mono">
                          {trackingData.access_code}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(trackingData.access_code);
                            toast.success('Access code copied');
                          }}
                          className="text-xs"
                        >
                          <Copy className="w-3 h-3 mr-1" />Code
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {/* User table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Acknowledged At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {trackingData.users.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-50/60">
                          <td className="px-4 py-2 font-medium text-slate-900">{u.name || '-'}</td>
                          <td className="px-4 py-2 text-slate-600">{u.email}</td>
                          <td className="px-4 py-2">
                            {u.status === 'acknowledged' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                                <CheckCircle className="w-3 h-3" />Acknowledged
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                                <Clock className="w-3 h-3" />Pending
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-500">
                            {u.acknowledged_at ? new Date(u.acknowledged_at).toLocaleString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={!!pdfPreviewOpen} onOpenChange={() => setPdfPreviewOpen(null)}>
        <DialogContent className="max-w-4xl bg-white h-[85vh] flex flex-col">
          {pdfPreviewOpen && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  {pdfPreviewOpen.title} — {pdfPreviewOpen.pdf_filename}
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 min-h-0">
                {(() => {
                  const params = new URLSearchParams();
                  if (viewingOrg?.id) params.set('view_tenant_id', viewingOrg.id);
                  if (authToken) params.set('token', authToken);
                  const qs = params.toString() ? `?${params.toString()}` : '';
                  return (
                    <iframe
                      src={`${API}/policy-ack/policies/${pdfPreviewOpen.id}/pdf${qs}`}
                      className="w-full h-full border rounded-lg"
                      title="Policy PDF"
                      data-testid="pdf-preview-iframe"
                    />
                  );
                })()}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Bulk Assign All Policies Dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => {
        if (!o) {
          setBulkOpen(false);
          setBulkResult(null);
          setBulkIncludeAllPolicies(true);
          setBulkSelectedPolicyIds([]);
          setBulkPolicySearch('');
        }
      }}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Bundle Acknowledgement Link</DialogTitle>
            <DialogDescription>
              Create a single acknowledgement URL covering the policies and recipients you choose.
            </DialogDescription>
          </DialogHeader>
          {!bulkResult ? (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Bundle name</label>
                <Input
                  value={bulkName}
                  onChange={(e) => setBulkName(e.target.value)}
                  placeholder="All Policies — 2026-05-06"
                  data-testid="bulk-name"
                />
              </div>

              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Policies to include</p>
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50">
                  <input
                    type="radio"
                    checked={bulkIncludeAllPolicies}
                    onChange={() => setBulkIncludeAllPolicies(true)}
                    className="mt-1"
                    data-testid="bulk-all-policies"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900">All policies ({policies.length})</p>
                    <p className="text-xs text-slate-500">Everyone acknowledges every policy in this organization.</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50">
                  <input
                    type="radio"
                    checked={!bulkIncludeAllPolicies}
                    onChange={() => setBulkIncludeAllPolicies(false)}
                    className="mt-1"
                    data-testid="bulk-pick-policies"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">Pick specific policies</p>
                    <p className="text-xs text-slate-500">Build a smaller bundle with just the policies you choose.</p>
                  </div>
                </label>
                {!bulkIncludeAllPolicies && (
                  <div className="mt-2 border rounded-lg">
                    <div className="p-2 border-b flex items-center gap-2">
                      <Search className="w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={bulkPolicySearch}
                        onChange={(e) => setBulkPolicySearch(e.target.value)}
                        placeholder="Search policies..."
                        className="flex-1 text-sm bg-transparent focus:outline-none"
                        data-testid="bulk-policy-search"
                      />
                      <button
                        type="button"
                        className="text-xs text-slate-600 hover:text-slate-900 underline"
                        onClick={() => {
                          const visible = policies.filter(p =>
                            !bulkPolicySearch ||
                            (p.title || '').toLowerCase().includes(bulkPolicySearch.toLowerCase()) ||
                            (p.policy_id || '').toLowerCase().includes(bulkPolicySearch.toLowerCase())
                          );
                          const allSelected = visible.every(p => bulkSelectedPolicyIds.includes(p.id));
                          if (allSelected) {
                            setBulkSelectedPolicyIds(prev => prev.filter(id => !visible.find(p => p.id === id)));
                          } else {
                            setBulkSelectedPolicyIds(prev => Array.from(new Set([...prev, ...visible.map(p => p.id)])));
                          }
                        }}
                        data-testid="bulk-policies-toggle-all"
                      >
                        {(() => {
                          const visible = policies.filter(p =>
                            !bulkPolicySearch ||
                            (p.title || '').toLowerCase().includes(bulkPolicySearch.toLowerCase()) ||
                            (p.policy_id || '').toLowerCase().includes(bulkPolicySearch.toLowerCase())
                          );
                          return visible.length && visible.every(p => bulkSelectedPolicyIds.includes(p.id))
                            ? 'Clear visible'
                            : 'Select visible';
                        })()}
                      </button>
                    </div>
                    <div className="max-h-56 overflow-auto divide-y">
                      {policies
                        .filter(p =>
                          !bulkPolicySearch ||
                          (p.title || '').toLowerCase().includes(bulkPolicySearch.toLowerCase()) ||
                          (p.policy_id || '').toLowerCase().includes(bulkPolicySearch.toLowerCase())
                        )
                        .map(p => (
                          <label
                            key={p.id}
                            className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer"
                            data-testid={`bulk-policy-row-${p.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={bulkSelectedPolicyIds.includes(p.id)}
                              onChange={(e) => {
                                if (e.target.checked) setBulkSelectedPolicyIds(prev => [...prev, p.id]);
                                else setBulkSelectedPolicyIds(prev => prev.filter(x => x !== p.id));
                              }}
                              className="h-4 w-4"
                              data-testid={`bulk-policy-cb-${p.id}`}
                            />
                            <span className="text-sm text-slate-900 flex-1 truncate">{p.title}</span>
                            {p.policy_id && (
                              <span className="text-xs text-slate-500 font-mono">{p.policy_id}</span>
                            )}
                          </label>
                        ))}
                      {policies.length === 0 && (
                        <p className="text-sm text-slate-500 p-3">No policies available in this organization yet.</p>
                      )}
                    </div>
                    <div className="p-2 border-t text-xs text-slate-600 flex items-center justify-between">
                      <span data-testid="bulk-policy-count">
                        {bulkSelectedPolicyIds.length} of {policies.length} selected
                      </span>
                      {bulkSelectedPolicyIds.length > 0 && (
                        <button
                          type="button"
                          className="text-slate-500 hover:text-slate-900 underline"
                          onClick={() => setBulkSelectedPolicyIds([])}
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Recipients</p>
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50">
                  <input
                    type="radio"
                    checked={bulkAssignAll}
                    onChange={() => setBulkAssignAll(true)}
                    className="mt-1"
                    data-testid="bulk-all-users"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900">All external users ({extUsers.length})</p>
                    <p className="text-xs text-slate-500">Assign to every imported user</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-slate-50">
                  <input
                    type="radio"
                    checked={!bulkAssignAll}
                    onChange={() => setBulkAssignAll(false)}
                    className="mt-1"
                    data-testid="bulk-pick-users"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">Pick specific users</p>
                  </div>
                </label>
                {!bulkAssignAll && (
                  <div className="mt-2 max-h-48 overflow-auto border rounded-lg divide-y">
                    {extUsers.map(u => (
                      <label key={u.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bulkSelectedUserIds.includes(u.id)}
                          onChange={(e) => {
                            if (e.target.checked) setBulkSelectedUserIds(prev => [...prev, u.id]);
                            else setBulkSelectedUserIds(prev => prev.filter(x => x !== u.id));
                          }}
                          className="h-4 w-4"
                        />
                        <span className="text-sm">{u.name || u.email}</span>
                        {u.name && <span className="text-xs text-slate-500">{u.email}</span>}
                      </label>
                    ))}
                    {extUsers.length === 0 && (
                      <p className="text-sm text-slate-500 p-3">No users imported yet. Import users first.</p>
                    )}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-cancel">Cancel</Button>
                <Button
                  onClick={handleBulkAssign}
                  disabled={
                    bulkSubmitting ||
                    policies.length === 0 ||
                    extUsers.length === 0 ||
                    (!bulkIncludeAllPolicies && bulkSelectedPolicyIds.length === 0)
                  }
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="bulk-confirm"
                >
                  {bulkSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : 'Create Bundle Link'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="font-semibold text-slate-900">Bundle created</p>
                <p className="text-sm text-slate-600 mt-1">
                  {bulkResult.total_policies} policies × {bulkResult.assigned_users} users
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Public Acknowledgement URL</label>
                <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 border">
                  <code className="text-xs text-slate-700 flex-1 truncate" data-testid="bulk-url">
                    {window.location.origin}/policy-acknowledge/bundle/{bulkResult.bundle_token}
                  </code>
                  <Button
                    size="sm"
                    onClick={() => copyBundleLink(bulkResult.bundle_token, bulkResult.access_code)}
                    className="bg-slate-900 text-white hover:bg-slate-800"
                    data-testid="bulk-copy"
                  >
                    <Copy className="w-3 h-3 mr-1" />Copy
                  </Button>
                </div>
                {bulkResult.access_code && (
                  <div className="mt-3">
                    <label className="text-xs font-medium text-slate-600 block mb-1">6-digit Access Code (required to open the link)</label>
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <code
                        className="text-2xl font-bold tracking-[0.35em] text-slate-900 flex-1 text-center font-mono"
                        data-testid="bulk-access-code"
                      >
                        {bulkResult.access_code}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(bulkResult.access_code);
                          toast.success('Access code copied');
                        }}
                        data-testid="bulk-copy-code"
                      >
                        <Copy className="w-3 h-3 mr-1" />Code
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Share both the link <strong>and</strong> the access code with recipients. They'll need to enter the code to view and acknowledge the policies.
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => window.open(`/policy-acknowledge/bundle/${bulkResult.bundle_token}`, '_blank')}
                  data-testid="bulk-preview"
                >
                  <ExternalLink className="w-3 h-3 mr-1" />Preview
                </Button>
                <Button onClick={() => { setBulkOpen(false); setBulkResult(null); }} className="bg-slate-900 text-white hover:bg-slate-800">
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bundle Tracking Dialog */}
      <Dialog open={!!bundleTrackingOpen} onOpenChange={() => setBundleTrackingOpen(null)}>
        <DialogContent className="sm:max-w-2xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bundle Tracking</DialogTitle>
            <DialogDescription>{bundleTrackingData?.bundle?.name}</DialogDescription>
          </DialogHeader>
          {bundleTrackingData ? (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Policies</p>
                  <p className="text-2xl font-bold text-slate-900">{bundleTrackingData.bundle?.policy_ids?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Users</p>
                  <p className="text-2xl font-bold text-slate-900">{bundleTrackingData.users.length}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Acknowledged</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {bundleTrackingData.users.filter(u => u.status === 'acknowledged').length}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-3">
                <Link2 className="w-4 h-4 text-slate-500" />
                <code className="text-xs text-slate-700 flex-1 truncate">
                  {window.location.origin}/policy-acknowledge/bundle/{bundleTrackingData.bundle?.bundle_token}
                </code>
                <Button size="sm" variant="outline" onClick={() => copyBundleLink(bundleTrackingData.bundle?.bundle_token, bundleTrackingData.bundle?.access_code)} className="text-xs">
                  <Copy className="w-3 h-3 mr-1" />Copy
                </Button>
              </div>
              {bundleTrackingData.bundle?.access_code && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <span className="text-xs font-medium text-slate-600 uppercase">Access code</span>
                  <code className="text-lg font-bold tracking-[0.35em] text-slate-900 flex-1 text-center font-mono">
                    {bundleTrackingData.bundle.access_code}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(bundleTrackingData.bundle.access_code);
                      toast.success('Access code copied');
                    }}
                    className="text-xs"
                  >
                    <Copy className="w-3 h-3 mr-1" />Code
                  </Button>
                </div>
              )}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Progress</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {bundleTrackingData.users.map((u) => (
                      <tr key={u.email} className="hover:bg-slate-50/60">
                        <td className="px-4 py-2 font-medium text-slate-900">{u.name || '-'}</td>
                        <td className="px-4 py-2 text-slate-600">{u.email}</td>
                        <td className="px-4 py-2 text-xs text-slate-700">{u.acknowledged} / {u.total}</td>
                        <td className="px-4 py-2">
                          {u.status === 'acknowledged' ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                              <CheckCircle className="w-3 h-3" />Acknowledged
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                              <Clock className="w-3 h-3" />Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {bundleTrackingData.users.length === 0 && (
                      <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-500">No users assigned to this bundle.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Manage External Users Dialog */}
      <Dialog open={manageUsersOpen} onOpenChange={setManageUsersOpen}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Manage External Users</DialogTitle>
            <DialogDescription>
              Imported users assigned to acknowledgements. Deleting a user removes pending assignments;
              completed acknowledgements are preserved for audit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 flex-1 min-h-0 flex flex-col">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="pl-10 bg-white"
                data-testid="manage-users-search"
              />
            </div>
            <div className="border rounded-lg overflow-y-auto flex-1">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Email</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 w-20">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {extUsers
                    .filter(u =>
                      !userSearch ||
                      (u.name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
                      (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
                    )
                    .map(u => (
                      <tr key={u.id} className="hover:bg-slate-50/60" data-testid={`manage-user-row-${u.id}`}>
                        <td className="px-3 py-2 font-medium text-slate-900">{u.name || '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{u.email}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => handleDeleteUser(u)}
                            disabled={deletingUserId === u.id}
                            className="text-red-600 hover:text-red-800 disabled:opacity-40"
                            title="Delete user"
                            data-testid={`delete-user-${u.id}`}
                          >
                            {deletingUserId === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  {extUsers.length === 0 && (
                    <tr><td colSpan={3} className="px-3 py-6 text-center text-sm text-slate-500">No external users imported yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setManageUsersOpen(false)} data-testid="manage-users-close">Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
